/**
 * **信用卡账单页**（批 `106-A` · `DR-061` · `AC-B2.27 ~ B2.36`）
 *
 * 入口：卡面「账单」按钮（`CreditCardSection`）· `/accounts/:accountId/bill` 直达。
 *
 * ## 🔴 本页的四条边界（端侧零口径）
 *
 * 1. **窗口不自己算** —— `windowStart` / `windowEnd` / `windowClosed` 全部服务端下发。
 *    账期是半开区间 + 月末 clamp（账单日 31 在 2 月按月末）：端侧按月份过滤就会错一期，
 *    而这正是"明细跟卡面的本期应还对不上"的唯一成因（`AC-B2.29`）；
 * 2. **金额不自加** —— 对账块的每个数字都是服务端给的 `summary` 字段，
 *    本页只做"搬到屏幕上"，**不做任何求和**（唯一的例外是展开子项的展示，它不进合计）；
 * 3. **行不允许再拆** —— `rows` 已按银行流水粒度合并（服务端 `toBillLegRows`），
 *    本页展开只是"看这笔钱怎么分的"，**合计不变**（`AC-B2.39`）；
 * 4. 🆕 **编辑不在本页做**（批 `109`）—— 点行去 `/new?editOf=<id>`，与流水抽屉的「编辑」
 *    同一个入口（`AC-C1.41`：编辑复用「记一笔」）。本页不复制录入表单，优惠行不给该入口。
 *
 * ## ⚠️ 与「账户流水」的分工
 *
 * | 视图 | 回答什么 |
 * |---|---|
 * | 本页 | 某**一期账单**里有哪些钱（与银行月结对得上） |
 * | `AccountLedgerDrawer`（账户流水） | 这张卡**全部历史**的进出（查全量用） |
 *
 * 两者都来自同一个合并内核 ⇒ 同一笔拆分在两处都显示为一个总额（`AC-B2.64`）。
 */

import { useState, type ReactElement } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { centsToYuanText, toCents, type CreditBillSummaryView, type CreditWaiverView } from '@jiaying/shared';
import { getCreditBillLegs, listCreditBills } from '../api/endpoints.js';
import { billReturnTo, readPeriod, type BillPeriod } from '../features/return-to.js';
import { Amount } from '../ui/Amount.js';
import { Loading } from '../ui/Loading.js';
import { CreditWaiverDeleteDialog, CreditWaiverDialog } from '../ui/CreditWaiverDialog.js';

/** 三档：`current` / `unbilled` / 往期的**账单日** `YYYY-MM-DD`（`BillPeriod` 在 `features/return-to.ts`） */

const KIND_TEXT: Record<string, string> = {
  spend: '消费',
  refund: '退款',
  repayment: '还款',
  waiver: '还款优惠',
};

export function CreditBillPage(): ReactElement {
  const { accountId = '' } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  /**
   * 🆕 批 `115`（`DR-066` §5 · `AC-B5.11`）：**分期在 URL 上**（`?period=current|unbilled|YYYY-MM-DD`）。
   *
   * 🔴 修前它是 `useState('current')`：从这张卡的「2026-09」改一笔流水再回来，
   * 页面会落在「本期」—— 用户得自己再点回那一期（负责人 2026-10-28 第 3 条，
   * 配合 `/new?returnTo=` 才算真正"改完回原地"）。
   * 🔴 合法性只认 `readPeriod()`（与 `returnTo` 白名单**同一判据**）：非法值收敛成 `current`，
   * 不是抛错、也不是当成往期去请求。
   */
  const [search, setSearch] = useSearchParams();
  const period: BillPeriod = readPeriod(search.get('period'));
  const setPeriod = (next: BillPeriod): void => {
    setSearch(
      (prev) => {
        const params = new URLSearchParams(prev);
        // 本期不带参数（URL 更短，也与"默认档"同义）
        if (next === 'current') params.delete('period');
        else params.set('period', next);
        return params;
      },
      // 🔴 `replace`：切 tab 不该在浏览器的"后退"里堆一串记录（用户按后退是要离开本页）
      { replace: true },
    );
  };
  // 🆕 批 `106-B`：优惠登记对话框（create = 新记 / edit = 改某条）+ 删除确认
  const [waiverDialog, setWaiverDialog] = useState<{ mode: 'create' | 'edit'; waiver?: CreditWaiverView } | null>(
    null,
  );
  const [waiverToDelete, setWaiverToDelete] = useState<CreditWaiverView | null>(null);

  const bills = useQuery({ queryKey: ['creditBills', 6], queryFn: () => listCreditBills(6) });

  const card = (bills.data?.items ?? []).find((c) => c.accountId === accountId) ?? null;
  // 🔴 往期列表由**服务端**给（6 期，`AC-B2.32`）；端侧不自行往前推月份
  const history = card?.history ?? [];

  const detail = useQuery({
    queryKey: ['creditBillLegs', accountId, period],
    queryFn: () => getCreditBillLegs(accountId, period),
    enabled: accountId !== '',
  });

  if (bills.isPending) return <LinearProgress data-testid="credit-bill-loading" />;
  if (bills.isError) {
    return <Alert severity="error">账单加载失败：{String(bills.error)}</Alert>;
  }
  if (card === null) {
    return (
      <Alert severity="warning" data-testid="credit-bill-missing">
        没有找到这张信用卡的账单 —— 它可能已被删除，或不属于当前账本。
      </Alert>
    );
  }

  /**
   * 🔴 选中哪个 tab = **`period` 本身**（`DR-066` §5 · `AC-B5.11`）。
   *
   * 修前这里写的是 `period === 'current' || 'unbilled' ? period : history[0]?.billDay`——
   * 在 `useState` 时代只能靠点 tab 改值，所以"任何往期都落到最新那一期"碰巧成立；
   * 但期次进了 URL 之后，这句话会把 `?period=2026-09-01` **高亮成 2026-10-01**
   * （数据查的是 09、tab 选的是 10）⇒ 必须直接用 `period`。
   * 若 period 不在最近 6 期里（用户手改地址栏），则**不高亮任何 tab** —— 诚实，不假装。
   */
  const tabValue = period;
  /**
   * 🆕 「记优惠」的归属期账单日（批 `106-B` · `AC-B2.50`）。
   * 🔴 未出账单**不给入口**（D5：未出账单不吃优惠 —— 服务端也会 400 挡）。
   * 当期 = `current.billDay`；往期 = tab 本身（它就是账单日）。
   */
  const waiverBillDay = period === 'current' ? (card.current?.billDay ?? null) : period === 'unbilled' ? null : period;

  return (
    <Stack spacing={2} data-testid="credit-bill-page">
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {card.name}
            {card.tailNumber !== null ? ` · 尾号 ${card.tailNumber}` : ''} · 账单
          </Typography>
          <Typography variant="caption" color="text.secondary">
            账单明细按<b>银行流水</b>粒度展示：一笔拆分是一条总额，与银行账单逐行对得上
          </Typography>
        </Box>
        <Button size="small" onClick={() => navigate('/accounts')} data-testid="credit-bill-back">
          返回账户
        </Button>
      </Stack>

      <Tabs
        value={tabValue}
        onChange={(_e, v: string) => setPeriod(v)}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab value="current" label="本期" data-testid="tab-current" />
        <Tab value="unbilled" label="未出" data-testid="tab-unbilled" />
        {history.map((h) => (
          <Tab key={h.billDay} value={h.billDay} label={h.billDay.slice(0, 7)} data-testid={`tab-${h.billDay}`} />
        ))}
      </Tabs>
      {/* 🔴 诚实边界：回溯窗口是规格 §8 的「最近 6 期」，超出不猜数字（`AC-B2.63`） */}
      <Typography variant="caption" color="text.secondary">
        仅回溯最近 {history.length} 期账单 —— 更早的账单不显示，也不对其金额做任何推测
      </Typography>

      <Divider />

      {/* ───────── 窗口与金额概览（服务端下发） ───────── */}
      {detail.isPending && <Loading py={3} />}
      {detail.isError && (
        <Alert severity="error" data-testid="credit-bill-detail-error">
          明细读取失败：{String(detail.error)}
        </Alert>
      )}
      {detail.isSuccess && detail.data.rows.length === 0 && (
        // 🔴 空期**整块不渲染对账块**（`AC-B2.36` / `DR-058` D1）：
        //    一行记录都没有时摆一排 ¥0.00，会被读成"这期一分钱不用还" —— 那是最坏的误导。
        <Typography variant="body2" color="text.secondary" data-testid="bill-rows-empty">
          这一期没有任何消费记录。
        </Typography>
      )}
      {detail.isSuccess &&
        detail.data.rows.length > 0 &&
        (() => {
          // 🔴 对账块的两个局部判定：`billed` = 是否已出账（未出账不给「本期金额」，`DR-064` D5）
          const summary = detail.data.summary;
          const summaryBilled = summary.due !== undefined;
          return (
            <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`账单日 ${detail.data.billDay}`} variant="outlined" />
            {detail.data.repaymentDate !== null ? (
              <Chip size="small" label={`还款日 ${detail.data.repaymentDate}`} variant="outlined" />
            ) : (
              <Chip size="small" label="未出账 · 暂无还款日" variant="outlined" />
            )}
            <Chip
              size="small"
              variant="outlined"
              label={`窗口 ${detail.data.windowStart} ~ ${detail.data.windowEnd}${detail.data.windowClosed ? '（含）' : ''}`}
              data-testid="credit-bill-window"
            />
          </Stack>

          {/* 🔴 对账块（`DR-064` D1 · `AC-B2.65` / `B2.72`）：四行加法，末行 = 本期金额。
              与卡面该期数字**逐项一致**（`AC-B2.33`）—— 全部字段由服务端下发，本页只搬。 */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }} data-testid="bill-recon">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              对账（这笔应还是怎么来的）
            </Typography>
            {summaryBilled ? (
              <>
                {/* ── 已出账：上期余额 + 本期流出 − 本期流入（− 优惠 − 分期移出 + 分期应还） ── */}
                {summary.openingBalance !== undefined && (
                  <ReconRow
                    label="上期余额"
                    cents={summary.openingBalance}
                    currency={card.currency}
                    testId="recon-opening"
                  />
                )}
                {summary.outflow !== undefined && (
                  <ReconRow label="本期流出" cents={summary.outflow} currency={card.currency} testId="recon-outflow" />
                )}
                {summary.inflow !== undefined && (
                  <ReconRow label="本期流入" cents={-summary.inflow} currency={card.currency} testId="recon-inflow" />
                )}
                <InflowBreakdown summary={summary} currency={card.currency} />
                {summary.waiver > 0 && (
                  <ReconRow label="还款优惠" cents={-summary.waiver} currency={card.currency} testId="recon-waiver" />
                )}
                {summary.installmentPrincipalDeducted !== undefined && (
                  <ReconRow
                    label="分期移出本金"
                    cents={-summary.installmentPrincipalDeducted}
                    currency={card.currency}
                    testId="recon-installment-principal"
                  />
                )}
                {summary.installmentDue !== undefined && (
                  <ReconRow
                    label="本期分期应还"
                    cents={summary.installmentDue}
                    currency={card.currency}
                    testId="recon-installment-due"
                  />
                )}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    本期金额
                  </Typography>
                  <Amount
                    cents={toCents(summary.due ?? 0)}
                    currency={card.currency}
                    sx={{ fontWeight: 700 }}
                    data-testid="recon-bill-amount"
                  />
                </Stack>
              </>
            ) : (
              <>
                {/* ── 未出账：只给流出 / 流入 / 净额，**不给「本期金额」**（`DR-064` D5 · `AC-B2.70`）── */}
                {summary.outflow !== undefined && (
                  <ReconRow label="本期流出" cents={summary.outflow} currency={card.currency} testId="recon-outflow" />
                )}
                {summary.inflow !== undefined && (
                  <ReconRow label="本期流入" cents={-summary.inflow} currency={card.currency} testId="recon-inflow" />
                )}
                <InflowBreakdown summary={summary} currency={card.currency} />
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    未出金额
                  </Typography>
                  <Amount
                    cents={toCents(summary.net ?? 0)}
                    currency={card.currency}
                    sx={{ fontWeight: 700 }}
                    data-testid="recon-unbilled-net"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  还没出账：这笔会被并进下期账单（下期金额 = 上期余额 + 这笔净额）
                </Typography>
              </>
            )}
            {/* ── 🆕 已还 / 剩余 / 状态（批 `106-B` · `AC-B2.58`）—— 服务端按数值推导，端侧只搬 ── */}
            {(() => {
              const paid =
                period === 'current'
                  ? card.current?.paid
                  : period === 'unbilled'
                    ? undefined
                    : (history.find((h) => h.billDay === period)?.paid ?? undefined);
              if (paid === undefined) return null;
              return (
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider', flexWrap: 'wrap', rowGap: 0.5 }}
                  data-testid="bill-paid-block"
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      账单日后已还
                    </Typography>
                    <Amount cents={toCents(paid.repaid)} currency={card.currency} colored={false} sx={{ fontWeight: 600 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      剩余
                    </Typography>
                    <Amount cents={toCents(paid.remaining)} currency={card.currency} colored={false} sx={{ fontWeight: 600 }} />
                  </Box>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={paid.status === 'paid' ? '已结清' : paid.status === 'partial' ? '部分已还' : '未还'}
                    data-testid="bill-paid-status"
                  />
                </Stack>
              );
            })()}
          </Box>

          {/* ───────── 🆕 该期优惠列表 + 记优惠入口（批 `106-B` · `AC-B2.50`）─────────
              🔴 未出账单不渲染（D5：未出账单不吃优惠 —— 服务端 `waivers` 也恒空数组） */}
          {waiverBillDay !== null && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }} data-testid="bill-waivers">
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  还款优惠（{String(detail.data.waivers.length)}）
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setWaiverDialog({ mode: 'create' });
                  }}
                  data-testid="bill-waiver-add"
                >
                  记优惠
                </Button>
              </Stack>
              {detail.data.waivers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  这期没有登记过优惠。银行给了还款金 / 立减 / 活动减免，记一笔就从本期应还里扣掉。
                </Typography>
              ) : (
                <Stack spacing={1} divider={<Divider flexItem />}>
                  {detail.data.waivers.map((w) => (
                    <Stack key={w.id} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {centsToYuanText(toCents(w.amount))}
                          {w.kind !== null && ` · ${w.kind}`}
                        </Typography>
                        {w.note !== null && (
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {w.note}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setWaiverDialog({ mode: 'edit', waiver: w });
                          }}
                          data-testid={`bill-waiver-edit-${w.id}`}
                        >
                          改
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setWaiverToDelete(w);
                          }}
                          data-testid={`bill-waiver-delete-${w.id}`}
                        >
                          删
                        </Button>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {/* ───────── 明细行 ───────── */}
          <Stack spacing={1} divider={<Divider flexItem />} data-testid="bill-rows">
            {detail.data.rows.map((row) => (
              <BillRow
                key={`${row.transactionId}-${row.direction}`}
                row={row}
                currency={card.currency}
                // 🆕 批 `109`：点这笔 ⇒ 去「记一笔」的编辑态（`AC-C1.41` 的同一入口）
                // 🆕 批 `115`（T3 · `AC-B5.10`）：**带上回程票** —— 保存后回到这张卡的**这一期**
                onEdit={() => {
                  navigate(
                    `/new?editOf=${encodeURIComponent(row.transactionId)}` +
                      `&returnTo=${encodeURIComponent(billReturnTo(accountId, period))}`,
                  );
                }}
              />
            ))}
          </Stack>

          {detail.data.truncated && (
            <Typography variant="caption" color="text.secondary">
              只显示最近 {detail.data.count} 行 —— 更早的请到「账户流水」查看。
            </Typography>
          )}
            </Stack>
          );
        })()}

      {/* ───────── 🆕 优惠登记 / 删除对话框（批 `106-B`）───────── */}
      {waiverDialog !== null && waiverBillDay !== null && (
        <CreditWaiverDialog
          mode={waiverDialog.mode}
          waiver={waiverDialog.waiver ?? null}
          accountId={accountId}
          currency={card.currency}
          billDay={waiverBillDay}
          onClose={() => {
            setWaiverDialog(null);
          }}
          onDone={() => {
            setWaiverDialog(null);
          }}
        />
      )}
      {waiverToDelete !== null && (
        <CreditWaiverDeleteDialog
          waiver={waiverToDelete}
          currency={card.currency}
          onClose={() => {
            setWaiverToDelete(null);
          }}
          onDone={() => {
            setWaiverToDelete(null);
          }}
        />
      )}
    </Stack>
  );
}

/**
 * 明细行：合并行可展开，展开**不改变任何合计**（`AC-B2.39`）。
 *
 * ## 🆕 批 `109`：点这一笔能改（负责人：「信用卡账单中，需要点击单笔交易能修改」）
 *
 * 编辑**不在本页重做一套表单** —— 直接跳到「记一笔」的编辑态（`/new?editOf=<id>`），
 * 与流水页详情抽屉的「编辑」是**同一个入口**（`AC-C1.41`：编辑复用「记一笔」，不做第二套录入形态）。
 * 行上同时给一个显式「改」按钮：整行可点对触屏友好，但**只靠整行可点是不自证的**
 * （用户看不出这里能点），按钮是那个可见的入口。
 *
 * 🔴 **优惠行例外**（`kind === 'waiver'`）：它是 `credit_bill_waivers` 那条优惠落在还款交易上的
 * **调整腿**（批 `106-B` 的"表行 + 1 腿同事务"）。点进「记一笔」会看到一笔转账，
 * 改它就会绕开「记优惠」的原子三件事（表行 / 腿 / 账单公式）⇒ 只在这里**不给编辑入口**，
 * 要改请走上方「还款优惠」区块的「改」。这是刻意排除，不是漏项。
 */
function BillRow({
  row,
  currency,
  onEdit,
}: {
  row: {
    transactionId: string;
    date: string;
    kind: string;
    direction: 'in' | 'out';
    amount: number;
    categoryId: string | null;
    label: string | null;
    note: string | null;
    legCount: number;
    children?: { categoryId: string | null; label: string | null; amount: number; note: string | null }[];
  };
  currency: string;
  /** 点这笔 ⇒ 去编辑（由页面注入；本组件不自己 `navigate`） */
  onEdit: () => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const merged = row.legCount > 1;
  /** 🔴 优惠行不给编辑入口（见上方组件注释） */
  const editable = row.kind !== 'waiver';
  const title =
    merged && row.categoryId === null
      ? `拆分 ${String(row.legCount)} 项`
      : (row.label ?? (row.categoryId === null ? '未分类' : row.categoryId));

  return (
    <Box
      data-testid={`bill-row-${row.transactionId}`}
      onClick={editable ? onEdit : undefined}
      sx={{ cursor: editable ? 'pointer' : 'default' }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
            {/* 🔴 四类归属由服务端判定（`linkedWaiverId` / `isRefundTxn` 等），端侧不重判 */}
            <Chip size="small" label={KIND_TEXT[row.kind] ?? row.kind} variant="outlined" />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">
            {row.date}
            {row.note !== null && row.note !== '' ? ` · ${row.note}` : ''}
            {merged ? ` · 合并 ${String(row.legCount)} 条成一笔` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Amount
            cents={toCents(row.direction === 'in' ? row.amount : -row.amount)}
            currency={currency}
            signed
            sx={{ fontSize: '0.875rem', fontWeight: 600 }}
          />
          {merged && (
            <Button
              size="small"
              sx={{ minWidth: 0, px: 0.5 }}
              onClick={(e) => {
                // 🔴 展开不是"编辑"：必须挡住冒泡，否则点展开就跳去改这笔（整行 onEdit）
                e.stopPropagation();
                setOpen(!open);
              }}
              data-testid={`bill-row-expand-${row.transactionId}`}
            >
              {open ? '收起' : '展开'}
            </Button>
          )}
          {editable && (
            <Button
              size="small"
              sx={{ minWidth: 0, px: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              data-testid={`bill-row-edit-${row.transactionId}`}
              title="改这一笔（走「记一笔」的编辑态）"
            >
              改
            </Button>
          )}
        </Stack>
      </Stack>
      {open && row.children !== undefined && (
        <Stack spacing={0.5} sx={{ mt: 1, pl: 2 }} data-testid="bill-row-children">
          {row.children.map((child, idx) => (
            <Stack
              key={`${child.categoryId ?? 'none'}-${String(child.amount)}-${String(idx)}`}
              direction="row"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                {child.label ?? (child.categoryId === null ? '未分类' : child.categoryId)}
              </Typography>
              <Amount
                cents={toCents(row.direction === 'in' ? child.amount : -child.amount)}
                currency={currency}
                signed
                sx={{ fontSize: '0.75rem' }}
              />
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/** 对账行 —— 🔴 `neutral = true` 表示这是"合计口径"而不是"减项"（不参与红绿着色） */
function ReconRow({
  label,
  cents,
  currency,
  neutral = false,
  testId,
}: {
  label: string;
  cents: number;
  currency: string;
  neutral?: boolean;
  testId?: string;
}): ReactElement {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }} data-testid={testId}>
      <Typography variant="body2" color={neutral ? 'text.primary' : 'text.secondary'}>
        {label}
      </Typography>
      <Amount cents={toCents(cents)} currency={currency} signed sx={{ fontSize: '0.875rem' }} />
    </Stack>
  );
}

/**
 * 「本期流入」的**拆分说明**（`DR-064` D1 · `AC-B2.68`）——
 * 一行小字挂在流入行下面，**不并成两个加减行**（那会把加法读乱）。
 *
 * 🔴 两个数字都从服务端来（`inflowRefund` / `inflowRepayment`），端侧不相加。
 */
function InflowBreakdown({
  summary,
  currency,
}: {
  summary: CreditBillSummaryView;
  currency: string;
}): ReactElement | null {
  const refund = summary.inflowRefund;
  const repayment = summary.inflowRepayment;
  if (refund === undefined || repayment === undefined) return null;
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="baseline"
      flexWrap="wrap"
      useFlexGap
      sx={{ pl: 1 }}
      data-testid="recon-inflow-breakdown"
    >
      <Typography variant="caption" color="text.secondary">
        其中 退款
      </Typography>
      <Amount cents={toCents(refund)} currency={currency} colored={false} sx={{ fontSize: '0.75rem' }} />
      <Typography variant="caption" color="text.secondary">
        · 还款
      </Typography>
      <Amount cents={toCents(repayment)} currency={currency} colored={false} sx={{ fontSize: '0.75rem' }} />
    </Stack>
  );
}
